/**
 * Shared pieces of the hollow candle overlays drawn over the CME candles on
 * the price pane (Vest NQ-PERP, utils/vestCandles; CME MES, utils/mesCandles).
 *
 * Both overlays start from native 2m bars that have already been moved onto
 * the chart's price axis (Vest: minus a rolling gap; MES: times a rolling
 * ratio), then fold them to the chart bar here exactly like the CME candles
 * (`floor(t / I) * I`). Below 2m each candle shows the 2m bar containing it.
 * A chart bar holding any unusable 2m bar (no prices, no gap / ratio) is
 * skipped, never filled in. Points are produced only at the chart's own candle
 * times: an extra time point would join the main time scale and shift every
 * logical index the volume pane is synced by.
 */

/** Native bar size of both overlay feeds and the CME candles, ms. */
export const OVERLAY_BAR_MS = 120_000;

export type Ohlc = [number, number, number, number];

export function median(xs: readonly number[]): number {
  if (xs.length === 0) return NaN;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * CME closes per 2m bucket (ms) from raw candles, last candle in a bucket
 * wins. The bucket holding `formingMs` is still open and left out.
 */
export function cmeClosedCloses(candles: readonly { timestamp: number; close: number }[], formingMs: number | null): Map<number, number> {
  const formingBucket = formingMs === null ? null : Math.floor(formingMs / OVERLAY_BAR_MS) * OVERLAY_BAR_MS;
  const last = new Map<number, { ts: number; close: number }>();
  for (const c of candles) {
    const ts = Number(c.timestamp);
    if (!Number.isFinite(ts) || typeof c.close !== 'number' || !Number.isFinite(c.close)) continue;
    const b = Math.floor(ts / OVERLAY_BAR_MS) * OVERLAY_BAR_MS;
    if (b === formingBucket) continue;
    const cur = last.get(b);
    if (!cur || ts >= cur.ts) last.set(b, { ts, close: c.close });
  }
  const out = new Map<number, number>();
  for (const b of [...last.keys()].sort((x, y) => x - y)) out.set(b, last.get(b)!.close);
  return out;
}

/** One native 2m bar, already on the chart's price axis. */
export interface PlacedBar {
  /** Bar open, ms. */
  t: number;
  /** Prices on the chart axis; null = the chart bar holding it is skipped. */
  px: Ohlc | null;
  /** The gap / ratio that placed it (carried to the candle for the label). */
  adj: number;
}

export interface FoldedCandle {
  /** Chart bar open, seconds. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  /** `adj` of the chart bar's last 2m bar. */
  adj: number;
  /** 2m bars folded in. */
  bars: number;
}

/**
 * Fold placed 2m bars to the chart's candle times (seconds, ascending).
 * `timeframeMinutes` is the chart's.
 */
export function foldPlacedBars(chartTimesSec: readonly number[], bars: Iterable<PlacedBar>, timeframeMinutes: number): FoldedCandle[] {
  if (chartTimesSec.length === 0) return [];
  const intervalMs = Math.max(1, Math.round(timeframeMinutes * 60_000));
  const spans = intervalMs < OVERLAY_BAR_MS;
  // Only bars that can land on a chart candle (feeds can hold history from
  // before the chart's first bar).
  const firstMs = Math.floor((chartTimesSec[0] * 1000) / OVERLAY_BAR_MS) * OVERLAY_BAR_MS;
  const inRange = [...bars].filter((b) => b.t >= firstMs).sort((x, y) => x.t - y.t);
  // Chart bar (seconds) -> its 2m bars, oldest first; null = unusable.
  const groups = new Map<number, PlacedBar[] | null>();
  for (const b of inRange) {
    const key = Math.floor((spans ? b.t : Math.floor(b.t / intervalMs) * intervalMs) / 1000);
    const g = groups.get(key);
    if (g === null) continue;
    if (!b.px) {
      groups.set(key, null);
      continue;
    }
    if (g) g.push(b);
    else groups.set(key, [b]);
  }
  const out: FoldedCandle[] = [];
  for (const time of chartTimesSec) {
    const key = spans ? Math.floor((Math.floor((time * 1000) / OVERLAY_BAR_MS) * OVERLAY_BAR_MS) / 1000) : time;
    const g = groups.get(key);
    if (!g || g.length === 0) continue;
    let open = NaN;
    let high = -Infinity;
    let low = Infinity;
    let close = NaN;
    let adj = NaN;
    for (const b of g) {
      const [o, h, l, c] = b.px!;
      if (Number.isNaN(open)) open = o;
      high = Math.max(high, h);
      low = Math.min(low, l);
      close = c;
      adj = b.adj;
    }
    // Bars placed by different gaps / ratios can leave the open/close a hair
    // outside the extremes; a candle must contain its own body.
    high = Math.max(high, open, close);
    low = Math.min(low, open, close);
    out.push({ time, open, high, low, close, adj, bars: g.length });
  }
  return out;
}

export interface OhlcPoint {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

const sameCandle = (a: OhlcPoint | undefined, b: OhlcPoint): boolean =>
  !!a && a.time === b.time && a.open === b.open && a.high === b.high && a.low === b.low && a.close === b.close;

/**
 * Indices to `update()` when only the last few candles changed or one was
 * appended; null when the series needs a full setData.
 */
export function candleTailUpdates(prev: readonly OhlcPoint[], next: readonly OhlcPoint[], maxTail = 3): number[] | null {
  if (prev.length === 0 || next.length < prev.length || next.length > prev.length + 1) return null;
  const from = Math.max(0, prev.length - maxTail);
  for (let i = 0; i < from; i++) if (!sameCandle(prev[i], next[i])) return null;
  const idx: number[] = [];
  for (let i = from; i < next.length; i++) {
    if (i < prev.length && prev[i].time !== next[i].time) return null;
    if (!sameCandle(prev[i], next[i])) idx.push(i);
  }
  return idx;
}
